<script setup lang="ts">
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useDeleteReferentUser } from "~/modules/relation/composables/useDeleteReferentUser";
import { useGetReferentUser } from "~/modules/relation/composables/useGetReferentUser";
import { useUpdateReferentUser } from "~/modules/relation/composables/useUpdateReferentUser";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";

const props = defineProps<GenericParameters>();

const { t } = useI18n();

const {
  isLoading: isLoadingFetchAllUsers,
  allResults: allUsers,
  error: errorFetchAllUsers,
  fetchAllData: fetchAllUsers,
} = useFetchUsers(props);

const {
  getReferentUser,
  isLoading: isLoadingReferentUser,
  result: referentUser,
} = useGetReferentUser(props);

const {
  updateReferentUser,
  isLoading: isLoadingUpdateReferentUser,
  error: errorUpdateReferentUser,
} = useUpdateReferentUser(props);

const {
  deleteReferentUser,
  isLoading: isLoadingDeleteReferentUser,
  error: errorDeleteReferentUser,
} = useDeleteReferentUser(props);

const selectedUserLogin: Ref<string | undefined> = ref();
const disableSaveButton = computed(() => {
  return (
    (!referentUser.value && !selectedUserLogin.value) ||
    (referentUser.value &&
      referentUser.value.login === selectedUserLogin.value) ||
    isLoadingUpdateReferentUser.value
  );
});

// edition
const edit = ref(false);

/**
 * Toggle edition
 * @param options
 *     edit = true to switch on edit mode, false otherwise
 *     getReferent = true to get the referent user of the entity, false otherwise
 *     fetchUsers = true to fetch the users of the entity, false otherwise
 */
async function toggleEdit(options: {
  edit: boolean;
  getReferent?: boolean;
  fetchUsers?: boolean;
}) {
  edit.value = options.edit;

  if (options.getReferent) {
    await getReferentUser();
  }

  // set the selected user on edit mode only
  if (edit.value) {
    selectedUserLogin.value = referentUser.value?.login;
  }

  if (options.fetchUsers && fetchAllUsers) {
    await fetchAllUsers();
  }
}

/**
 * Save the selected user as referent user
 */
async function save() {
  if (selectedUserLogin.value) {
    await updateReferentUser(selectedUserLogin.value);

    // on update failed
    if (errorUpdateReferentUser.value) {
      errorUpdateReferentUser.value.notify(NotificationKey.saveError);
    }
    // on update succeed
    else {
      showNotificationSuccess(t(NotificationKey.saveSuccess));

      // try to get the user from the user list
      let user;
      if (allUsers) {
        user = allUsers.value?.find(
          (user) => user.login === selectedUserLogin.value,
        );
      }

      // set the referent user if possible
      referentUser.value = user
        ? {
            login: user.login,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
          }
        : undefined;

      // switch to display mode, only get the referent user if the previous set of the referent user has failed
      toggleEdit({ edit: false, getReferent: !referentUser.value });
    }
  } else if (referentUser.value && !selectedUserLogin.value) {
    await deleteReferentUser();

    if (errorDeleteReferentUser.value) {
      errorDeleteReferentUser.value.notify(NotificationKey.removeError);
    } else {
      showNotificationSuccess(t(NotificationKey.removeSuccess));
      referentUser.value = undefined;
    }
  }
}

onMounted(async () => {
  // get and display the referent user by default
  await toggleEdit({
    edit: false,
    getReferent: true,
  });

  // if no referent user, fetch the users and switch on edit mode
  if (!referentUser.value) {
    await toggleEdit({
      edit: true,
      fetchUsers: true,
    });
  }
});
</script>

<template>
  <container-component :px="0" :py="0" :is-loading="isLoadingReferentUser">
    <h4 class="text-gray-900">
      {{ $t("administration.relations.contact.referentUser.title") }}
    </h4>

    <p class="font-semibold text-gray-600 mb-4">
      {{ $t("administration.relations.contact.referentUser.description") }}
    </p>

    <relation-referent-user-details
      v-if="!edit"
      :referent-user="referentUser"
      @change-referent-user="toggleEdit({ edit: true, fetchUsers: !allUsers })"
    />

    <div v-else>
      <lxc-select
        v-model="selectedUserLogin"
        select-filter
        :placeholder="
          $t('administration.relations.contact.referentUser.selectUser')
        "
        :select-search-placeholder="$t('user.search')"
        :select-no-results="$t('input.select.search.noResults')"
        :is-loading-options="isLoadingFetchAllUsers"
        :error="errorFetchAllUsers?.toError()"
      >
        <lxc-option
          v-for="(user, index) in allUsers"
          :key="index"
          :label="`${user.lastName} ${user.firstName}`"
          :value="user.login"
        >
          <div
            class="grid w-full grid-cols-[1fr_1fr_2fr] gap-4 justify-items-start"
          >
            <div>{{ user.lastName }}</div>
            <div>{{ user.firstName }}</div>
            <div>{{ user.email }}</div>
          </div>
        </lxc-option>
      </lxc-select>

      <div class="mt-6 flex gap-4">
        <lxc-button
          :title="$t('button.save')"
          :text="$t('button.save')"
          :is-loading="
            isLoadingUpdateReferentUser || isLoadingDeleteReferentUser
          "
          :disabled="disableSaveButton"
          @click="save"
        />

        <lxc-button
          v-if="edit && referentUser"
          :title="$t('button.cancel')"
          :text="$t('button.cancel')"
          type="tertiary"
          @click="toggleEdit({ edit: false })"
        />
      </div>
    </div>
  </container-component>
</template>
